import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import * as authentikOidc from '../auth/authentikOidc';
import { useSubscription } from '../hooks/useSubscription';
import './AccountManagement.css';

export function AccountManagement() {
  const { user } = useUser();
  const navigate = useNavigate();
  const {
    subscription,
    hasSubscription,
    isTrialing,
    daysRemaining,
    status,
    isActive,
    loading,
    startCheckout,
    openCustomerPortal,
    syncSubscription,
  } = useSubscription();

  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      await openCustomerPortal();
    } catch (err) {
      alert('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setIsLoading(true);
    try {
      await startCheckout('personal');
    } catch (err) {
      alert('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    setIsSyncing(true);
    try {
      await syncSubscription();
    } catch (err) {
      alert('Kon abonnement niet synchroniseren. Probeer het opnieuw.');
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'trialing': return 'schedule';
      case 'active': return 'check_circle';
      case 'past_due': return 'warning';
      case 'canceled': return 'cancel';
      case 'paused': return 'pause_circle';
      default: return 'help_outline';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'trialing': return 'Trial actief';
      case 'active': return 'Actief';
      case 'past_due': return 'Betaling mislukt';
      case 'canceled': return 'Geannuleerd';
      case 'paused': return 'Gepauzeerd';
      default: return 'Onbekend';
    }
  };

  if (loading) {
    return (
      <div className="account-page">
        <div className="account-loading">
          <div className="spinner"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Header */}
        <div className="account-header">
          <button
            className="account-back-btn"
            onClick={() => navigate('/app')}
            aria-label="Terug naar chat"
          >
            <span className="material-icons">arrow_back</span>
          </button>
          <h1 className="account-header__title">Mijn Account</h1>
        </div>

        {/* User Profile */}
        <div className="account-section">
          <div className="account-section__header">
            <span className="material-icons">person</span>
            <h2 className="account-section__title">Profiel</h2>
          </div>
          <div className="account-card">
            <div className="account-profile">
              <div className="account-avatar">
                {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
              </div>
              <div className="account-profile__info">
                <p className="account-profile__name">{user?.name || 'Niet ingesteld'}</p>
                <p className="account-profile__email">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="account-section">
          <div className="account-section__header">
            <span className="material-icons">credit_card</span>
            <h2 className="account-section__title">Abonnement</h2>
            {hasSubscription && (
              <button
                className="account-sync-btn"
                onClick={handleSyncSubscription}
                disabled={isSyncing}
                title="Vernieuw status"
              >
                <span className={`material-icons ${isSyncing ? 'spinning' : ''}`}>sync</span>
              </button>
            )}
          </div>

          {!hasSubscription ? (
            <div className="account-card account-card--cta">
              <div className="account-empty">
                <span className="material-icons account-empty__icon">diamond</span>
                <h3>Geen actief abonnement</h3>
                <p>Activeer je abonnement om alle functies te gebruiken</p>
                <button
                  className="account-btn account-btn--primary"
                  onClick={handleStartTrial}
                  disabled={isLoading}
                >
                  <span className="material-icons">rocket_launch</span>
                  {isLoading ? 'Laden...' : 'Activeer Abonnement'}
                </button>
              </div>
            </div>
          ) : (
            <div className="account-card">
              <div className="account-subscription">
                <div className="account-subscription__grid">
                  <div className="account-field">
                    <label>Plan</label>
                    <p className="account-field--highlight">Personal Plan</p>
                  </div>

                  <div className="account-field">
                    <label>Prijs</label>
                    <p>€49 per maand</p>
                  </div>

                  <div className="account-field">
                    <label>Status</label>
                    <p>
                      <span className={`account-status account-status--${status}`}>
                        <span className="material-icons">{getStatusIcon()}</span>
                        {getStatusText()}
                      </span>
                    </p>
                  </div>

                  {subscription?.created_at && (
                    <div className="account-field">
                      <label>Gestart op</label>
                      <p>{formatDate(subscription.created_at)}</p>
                    </div>
                  )}
                </div>

                {isTrialing && (
                  <div className="account-trial-banner">
                    <span className="material-icons">timer</span>
                    <div className="account-trial-banner__content">
                      <strong>{daysRemaining} {daysRemaining === 1 ? 'dag' : 'dagen'} resterend</strong>
                      <span>Trial eindigt op {formatDate(subscription?.trial_end)}</span>
                    </div>
                  </div>
                )}

                {isActive && !isTrialing && subscription?.current_period_end && (
                  <div className="account-next-billing">
                    <span className="material-icons">event</span>
                    <span>Volgende factuurdatum: {formatDate(subscription.current_period_end)}</span>
                  </div>
                )}

                <div className="account-actions">
                  {subscription?.auto_trial ? (
                    <button
                      className="account-btn account-btn--primary"
                      onClick={handleStartTrial}
                      disabled={isLoading}
                    >
                      <span className="material-icons">credit_card</span>
                      {isLoading ? 'Laden...' : 'Betaalgegevens Toevoegen'}
                    </button>
                  ) : (
                    <>
                      <button
                        className="account-btn account-btn--secondary"
                        onClick={handleManageSubscription}
                        disabled={isLoading}
                      >
                        <span className="material-icons">settings</span>
                        {isLoading ? 'Laden...' : 'Beheer Abonnement'}
                      </button>
                      <a
                        href="https://billing.stripe.com/p/login/28EeVfcol3Z2bD4af76kg00"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="account-btn account-btn--outline"
                      >
                        <span className="material-icons">receipt_long</span>
                        Facturen Bekijken
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="account-section">
          <div className="account-section__header">
            <span className="material-icons">help_outline</span>
            <h2 className="account-section__title">Hulp & Support</h2>
          </div>
          <div className="account-card">
            <div className="account-help-links">
              <a
                href="mailto:info@humanaize.nl?subject=Vraag over Humanaize Matcher"
                className="account-help-link"
              >
                <span className="material-icons">mail</span>
                <span>Contact opnemen</span>
                <span className="material-icons account-help-link__arrow">chevron_right</span>
              </a>
              <a
                href="mailto:pim.sudmeier@humanaize.nl?subject=Enterprise Plan - Humanaize Matcher"
                className="account-help-link"
              >
                <span className="material-icons">business</span>
                <span>Enterprise opties</span>
                <span className="material-icons account-help-link__arrow">chevron_right</span>
              </a>
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="account-section">
          <button
            className="account-btn account-btn--danger"
            onClick={() => authentikOidc.logout()}
          >
            <span className="material-icons">logout</span>
            Uitloggen
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountManagement;